import { useMemo } from 'react'
import { NoData } from 'src/components/analytics/common/NoData'
import BreakdownTable from 'src/components/analytics/tables/BreakdownTable'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { formatNumberToTwoDecimals, percentageWithFixedDecmials } from 'src/lib/utils/analytics'

type CodToPrepaidData = {
  c2p_percentage: {
    label: 'c2p_percentage'
    currentValue: number
    percentageChange: number
  }
  discount_burn: {
    label: 'discount_burn'
    currentValue: number
    percentageChange: number
  }
  gmv_processed: {
    label: 'gmv_processed'
    currentValue: number
    percentageChange: number
  }
}

type RawCodToPrepaidData = {
  dimension: 'c2p_percentage' | 'discount_burn' | 'gmv_processed'
  data: { x: number }[]
}

export default function CodOrdersConvertedToPrepaidTable() {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.cod_orders_converted_to_prepaid,
      dependencies: {
        endpoints: ['/cod-to-prepaid']
      }
    },
    refreshKey
  })

  const codToPrepaidData: CodToPrepaidData = useMemo(() => {
    const rawData: RawCodToPrepaidData[] = data[0]?.data?.result?.widgetData?.data
    const rawComparedData: RawCodToPrepaidData[] = data[0]?.data?.result?.widgetData?.comparedPreviousData
    if (!rawData || !rawComparedData) {
      return {
        c2p_percentage: {
          label: 'c2p_percentage',
          currentValue: 0,
          percentageChange: 0
        },
        discount_burn: {
          label: 'discount_burn',
          currentValue: 0,
          percentageChange: 0
        },
        gmv_processed: {
          label: 'gmv_processed',
          currentValue: 0,
          percentageChange: 0
        }
      }
    }

    const finalResult: CodToPrepaidData = rawData.reduce(
      (acc, item) => {
        if (item.dimension === 'c2p_percentage') {
          acc.c2p_percentage = {
            label: 'c2p_percentage',
            currentValue: formatNumberToTwoDecimals(Number(item.data[0].x)),
            percentageChange: formatNumberToTwoDecimals(Number(item.data[0].x))
          }
        } else if (item.dimension === 'discount_burn') {
          acc.discount_burn = {
            label: 'discount_burn',
            currentValue: formatNumberToTwoDecimals(Number(item.data[0].x)),
            percentageChange: formatNumberToTwoDecimals(Number(item.data[0].x))
          }
        } else if (item.dimension === 'gmv_processed') {
          acc.gmv_processed = {
            label: 'gmv_processed',
            currentValue: formatNumberToTwoDecimals(Number(item.data[0].x)),
            percentageChange: formatNumberToTwoDecimals(Number(item.data[0].x))
          }
        }
        return acc
      },
      {
        c2p_percentage: {
          label: 'c2p_percentage',
          currentValue: 0,
          percentageChange: 0
        },
        discount_burn: {
          label: 'discount_burn',
          currentValue: 0,
          percentageChange: 0
        },
        gmv_processed: {
          label: 'gmv_processed',
          currentValue: 0,
          percentageChange: 0
        }
      }
    )

    if (rawComparedData) {
      const finalComparedResult: CodToPrepaidData = rawComparedData.reduce(
        (acc, item) => {
          if (item.dimension === 'c2p_percentage') {
            acc.c2p_percentage = {
              label: 'c2p_percentage',
              currentValue: formatNumberToTwoDecimals(Number(item.data[0].x)),
              percentageChange: percentageWithFixedDecmials(Number(item.data[0].x), Number(item.data[0].x))
            }
          } else if (item.dimension === 'discount_burn') {
            acc.discount_burn = {
              label: 'discount_burn',
              currentValue: formatNumberToTwoDecimals(Number(item.data[0].x)),
              percentageChange: percentageWithFixedDecmials(Number(item.data[0].x), Number(item.data[0].x))
            }
          } else if (item.dimension === 'gmv_processed') {
            acc.gmv_processed = {
              label: 'gmv_processed',
              currentValue: formatNumberToTwoDecimals(Number(item.data[0].x)),
              percentageChange: percentageWithFixedDecmials(Number(item.data[0].x), Number(item.data[0].x))
            }
          }
          return acc
        },
        {
          c2p_percentage: {
            label: 'c2p_percentage',
            currentValue: 0,
            percentageChange: 0
          },
          discount_burn: {
            label: 'discount_burn',
            currentValue: 0,
            percentageChange: 0
          },
          gmv_processed: {
            label: 'gmv_processed',
            currentValue: 0,
            percentageChange: 0
          }
        }
      )

      finalResult.c2p_percentage = {
        ...finalResult.c2p_percentage,
        percentageChange: formatNumberToTwoDecimals(
          finalResult.c2p_percentage.currentValue - finalComparedResult.c2p_percentage.currentValue
        )
      }
      finalResult.discount_burn = {
        ...finalResult.discount_burn,
        percentageChange: percentageWithFixedDecmials(
          finalResult.discount_burn.currentValue,
          finalComparedResult.discount_burn.currentValue
        )
      }
      finalResult.gmv_processed = {
        ...finalResult.gmv_processed,
        percentageChange: percentageWithFixedDecmials(
          finalResult.gmv_processed.currentValue,
          finalComparedResult.gmv_processed.currentValue
        )
      }
    }

    return finalResult
  }, [data])

  const breakdownData = [
    {
      label: 'GMV processed',
      value: `₹${codToPrepaidData.gmv_processed.currentValue}`,
      change: codToPrepaidData.gmv_processed.percentageChange
    },
    {
      label: '% of COD orders converted to prepaid',
      value: `${codToPrepaidData.c2p_percentage.currentValue}%`,
      change: codToPrepaidData.c2p_percentage.percentageChange
    },
    {
      label: 'Discount burn',
      value: `₹${codToPrepaidData.discount_burn.currentValue}`,
      change: codToPrepaidData.discount_burn.percentageChange
    }
  ]

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (codToPrepaidData) {
      return (
        <div className='flex flex-col h-full'>
          <div className='text-sm'>
            <div className='flex items-center gap-2 pt-4'>
              <span className='font-bold text-[18px]'>{codToPrepaidData.c2p_percentage.currentValue}%</span>
              <span className='font-bold text-[18px]'>
                {/* <TrendIndicator
                  value={codToPrepaidData.c2p_percentage.percentageChange}
                  formatter={value => `${value}%`}
                /> */}
              </span>
            </div>
            <p className='text-coal-slate text-xs py-2'>Percentage of COD orders converted to prepaid</p>
          </div>
          <div className='flex-1 pt-4'>
            <BreakdownTable data={breakdownData} />
          </div>
        </div>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full overflow-y-auto'>
      {renderComponent()}
    </div>
  )
}
